import type { CitationOutput } from "@/components/tool-kit/source-citation-builder";
import {
  type CitationParts,
  mlaDate,
  parsePartialDate,
  tidyParts,
} from "@/lib/tool-kit/citation-parts";
import {
  closer,
  get,
  italic,
  type LegalValues,
  mainTitle,
  mlaUrl,
  plain,
  romanNumeral,
} from "@/lib/tool-kit/legal-sources/common";

/*
 * Constitution citations — U.S. and state — in APA 7, MLA 9, Chicago 18 and the
 * Bluebook.
 *
 * Sources checked (October 2026):
 * - APA 7 §11.9, "Constitutions and Charters" (Publication Manual pp. 366–368), as
 *   reproduced by Liberty University's APA 7 legal table, Columbus State's APA 7 legal
 *   page and Waubonsee's "Citing Constitutions in APA Style": a whole federal or state
 *   constitution needs no reference-list entry ("simply refer to the constitution in
 *   text"); articles and amendments get a reference entry and an in-text citation —
 *   "U.S. Const. art. I, § 3.", "U.S. Const. amend. XIX.", "S.C. Const. art. XI, § 3."
 *   No date unless the provision is repealed: "U.S. Const. amend. XVIII (repealed
 *   1933)." Narrative forms: "Article I, Section 3, of the U.S. Constitution",
 *   "Amendment XIX to the U.S. Constitution". Article and amendment numbers of the U.S.
 *   Constitution are Roman; state amendment numbers are Arabic.
 * - Bluebook 22d ed. rule 11 and Bluepages B11 (Cornell LII, Introduction to Basic
 *   Legal Citation §2-310; Kirwin's Bluebook excerpts; Georgetown's Bluebook guide):
 *   "U.S. Const. art. I, § 9, cl. 2." — no punctuation after "Const.", commas between
 *   subdivisions, nothing italicized, no date unless repealed or amended ("(repealed
 *   1933)", "(amended 1913)"), state names abbreviated per table T10 ("Cal. Const.").
 *   Law reviews set the name in small capitals; there is no short form except id.
 * - Chicago 18 §14.181 (constitutions), with §14.170: Chicago's advice for legal
 *   documents relies on the Bluebook (CMOS Q&A, "Laws and statutes", 2025). Legal
 *   documents are cited in notes, not in the bibliography (Portland and Lone Star
 *   CMOS 18 guides).
 * - MLA 9: MLA Style Center, "Documenting Legal Works in MLA Style" — cite the version
 *   you consulted, e.g. "The Constitution of the United States: A Transcription.
 *   National Archives, U.S. National Archives and Records Administration, 28 Feb. 2017,
 *   www.archives.gov/founding-docs/constitution-transcript." The in-text reference keys
 *   to that entry; "use Roman numerals for divisions of legal works that use them".
 */

export const CONSTITUTION_FIELDS = {
  jurisdiction: "jurisdiction",
  part: "part",
  number: "number",
  section: "section",
  clause: "clause",
  status: "status",
  statusYear: "statusYear",
  versionTitle: "versionTitle",
  website: "website",
  publisher: "publisher",
  date: "date",
  url: "url",
} as const;

const F = CONSTITUTION_FIELDS;

export type ConstitutionPart = "article" | "amendment" | "preamble" | "whole";
export type ConstitutionStatus = "inForce" | "repealed" | "amended";

/** Bluebook table T10 abbreviations, with the state's name for APA's narrative form. */
export const STATE_CONSTITUTIONS: Readonly<
  Record<string, { name: string; abbreviation: string }>
> = {
  al: { name: "Alabama", abbreviation: "Ala." },
  ak: { name: "Alaska", abbreviation: "Alaska" },
  az: { name: "Arizona", abbreviation: "Ariz." },
  ar: { name: "Arkansas", abbreviation: "Ark." },
  ca: { name: "California", abbreviation: "Cal." },
  co: { name: "Colorado", abbreviation: "Colo." },
  ct: { name: "Connecticut", abbreviation: "Conn." },
  de: { name: "Delaware", abbreviation: "Del." },
  fl: { name: "Florida", abbreviation: "Fla." },
  ga: { name: "Georgia", abbreviation: "Ga." },
  hi: { name: "Hawaii", abbreviation: "Haw." },
  id: { name: "Idaho", abbreviation: "Idaho" },
  il: { name: "Illinois", abbreviation: "Ill." },
  in: { name: "Indiana", abbreviation: "Ind." },
  ia: { name: "Iowa", abbreviation: "Iowa" },
  ks: { name: "Kansas", abbreviation: "Kan." },
  ky: { name: "Kentucky", abbreviation: "Ky." },
  la: { name: "Louisiana", abbreviation: "La." },
  me: { name: "Maine", abbreviation: "Me." },
  md: { name: "Maryland", abbreviation: "Md." },
  ma: { name: "Massachusetts", abbreviation: "Mass." },
  mi: { name: "Michigan", abbreviation: "Mich." },
  mn: { name: "Minnesota", abbreviation: "Minn." },
  ms: { name: "Mississippi", abbreviation: "Miss." },
  mo: { name: "Missouri", abbreviation: "Mo." },
  mt: { name: "Montana", abbreviation: "Mont." },
  ne: { name: "Nebraska", abbreviation: "Neb." },
  nv: { name: "Nevada", abbreviation: "Nev." },
  nh: { name: "New Hampshire", abbreviation: "N.H." },
  nj: { name: "New Jersey", abbreviation: "N.J." },
  nm: { name: "New Mexico", abbreviation: "N.M." },
  ny: { name: "New York", abbreviation: "N.Y." },
  nc: { name: "North Carolina", abbreviation: "N.C." },
  nd: { name: "North Dakota", abbreviation: "N.D." },
  oh: { name: "Ohio", abbreviation: "Ohio" },
  ok: { name: "Oklahoma", abbreviation: "Okla." },
  or: { name: "Oregon", abbreviation: "Or." },
  pa: { name: "Pennsylvania", abbreviation: "Pa." },
  ri: { name: "Rhode Island", abbreviation: "R.I." },
  sc: { name: "South Carolina", abbreviation: "S.C." },
  sd: { name: "South Dakota", abbreviation: "S.D." },
  tn: { name: "Tennessee", abbreviation: "Tenn." },
  tx: { name: "Texas", abbreviation: "Tex." },
  ut: { name: "Utah", abbreviation: "Utah" },
  vt: { name: "Vermont", abbreviation: "Vt." },
  va: { name: "Virginia", abbreviation: "Va." },
  wa: { name: "Washington", abbreviation: "Wash." },
  wv: { name: "West Virginia", abbreviation: "W. Va." },
  wi: { name: "Wisconsin", abbreviation: "Wis." },
  wy: { name: "Wyoming", abbreviation: "Wyo." },
};

export interface ConstitutionLabels {
  apa: string;
  mla: string;
  chicago: string;
  bluebook: string;
  referenceList: string;
  parenthetical: string;
  narrative: string;
  worksCited: string;
  inText: string;
  footnote: string;
  citation: string;
  apaNote: string;
  apaWholeNote: string;
  mlaNote: string;
  mlaNeedsVersion: string;
  chicagoNote: string;
  bluebookNote: string;
  legalWholeNote: string;
}

interface Provision {
  part: ConstitutionPart;
  isFederal: boolean;
  /** "U.S. Const." or "Cal. Const." */
  prefix: string;
  /** "U.S. Constitution" or "California Constitution" — APA's narrative form. */
  name: string;
  /** Roman for articles and U.S. amendments; state amendments as typed. */
  number: string;
  section: string;
  clause: string;
  status: ConstitutionStatus;
  statusYear: string;
}

function readProvision(values: LegalValues): Provision {
  const code = get(values, F.jurisdiction) || "us";
  const state = STATE_CONSTITUTIONS[code];
  const part = (get(values, F.part) || "article") as ConstitutionPart;
  const rawNumber = get(values, F.number);
  const isFederal = !state;
  const romanize = part === "article" || isFederal;
  const status = (get(values, F.status) || "inForce") as ConstitutionStatus;
  return {
    part,
    isFederal,
    prefix: state ? `${state.abbreviation} Const.` : "U.S. Const.",
    name: state ? `${state.name} Constitution` : "U.S. Constitution",
    number: romanize ? romanNumeral(rawNumber) : rawNumber,
    section: get(values, F.section),
    clause: get(values, F.clause),
    status,
    statusYear: get(values, F.statusYear),
  };
}

/** "art. I, § 8, cl. 3" / "amend. XIV, § 1" / "pmbl." */
function legalLocator(provision: Provision): string {
  if (provision.part === "preamble") {
    return "pmbl.";
  }
  const head =
    provision.part === "amendment"
      ? `amend. ${provision.number}`
      : `art. ${provision.number}`;
  const pieces = [head];
  if (provision.section) {
    pieces.push(`§ ${provision.section}`);
  }
  if (provision.clause) {
    pieces.push(`cl. ${provision.clause}`);
  }
  return pieces.join(", ");
}

/** "repealed 1933" / "amended 1913" — empty for a provision in force. */
function statusPhrase(provision: Provision): string {
  if (provision.status === "inForce" || provision.statusYear === "") {
    return "";
  }
  const word = provision.status === "repealed" ? "repealed" : "amended";
  return `${word} ${provision.statusYear}`;
}

/** "U.S. Const. art. I, § 8, cl. 3 (amended 1913)" — Bluebook, APA and Chicago. */
function legalConstitutionCite(provision: Provision): string {
  const status = statusPhrase(provision);
  const base = `${provision.prefix} ${legalLocator(provision)}`;
  return status ? `${base} (${status})` : base;
}

/** APA's narrative citation: "Article I, Section 8, Clause 3, of the U.S. Constitution". */
function apaNarrative(provision: Provision): string {
  const status = statusPhrase(provision);
  const suffix = status ? ` (${status})` : "";
  if (provision.part === "preamble") {
    return `the Preamble to the ${provision.name}${suffix}`;
  }
  const subdivisions = [
    provision.section ? `Section ${provision.section}` : "",
    provision.clause ? `Clause ${provision.clause}` : "",
  ].filter(Boolean);
  if (provision.part === "amendment") {
    const amendment = `Amendment ${provision.number} to the ${provision.name}`;
    if (subdivisions.length === 0) {
      return `${amendment}${suffix}`;
    }
    const joiner = subdivisions.length > 1 ? ", of " : " of ";
    return `${subdivisions.join(", ")}${joiner}${amendment}${suffix}`;
  }
  const lead = [`Article ${provision.number}`, ...subdivisions].join(", ");
  const joiner = subdivisions.length > 0 ? ", of the " : " of the ";
  return `${lead}${joiner}${provision.name}${suffix}`;
}

function apaOutput(
  provision: Provision,
  labels: ConstitutionLabels
): CitationOutput {
  if (provision.part === "whole") {
    return {
      key: "apa",
      label: labels.apa,
      entries: [],
      note: labels.apaWholeNote,
    };
  }
  const cite = legalConstitutionCite(provision);
  const status = statusPhrase(provision);
  const parenthetical = status
    ? `(${provision.prefix} ${legalLocator(provision)}, ${status})`
    : `(${cite})`;
  return {
    key: "apa",
    label: labels.apa,
    entries: [
      { label: labels.referenceList, parts: [plain(`${cite}.`)] },
      { label: labels.parenthetical, parts: [plain(parenthetical)] },
      { label: labels.narrative, parts: [plain(apaNarrative(provision))] },
    ],
    note: labels.apaNote,
  };
}

/** MLA's in-text locator: "art. I, sec. 8, cl. 3". */
function mlaLocator(provision: Provision): string {
  if (provision.part === "whole") {
    return "";
  }
  if (provision.part === "preamble") {
    return "preamble";
  }
  const head =
    provision.part === "amendment"
      ? `amend. ${provision.number}`
      : `art. ${provision.number}`;
  return [
    head,
    provision.section ? `sec. ${provision.section}` : "",
    provision.clause ? `cl. ${provision.clause}` : "",
  ]
    .filter(Boolean)
    .join(", ");
}

const RE_LEADING_ARTICLE = /^(the|a|an)\s+/i;

/** MLA shortens a title in text: subtitle and leading article dropped. */
function mlaShortTitle(title: string): string {
  return mainTitle(title).replace(RE_LEADING_ARTICLE, "");
}

function mlaOutput(
  provision: Provision,
  values: LegalValues,
  labels: ConstitutionLabels
): CitationOutput {
  const title = get(values, F.versionTitle);
  if (title === "") {
    return {
      key: "mla",
      label: labels.mla,
      entries: [],
      note: labels.mlaNeedsVersion,
    };
  }
  const website = get(values, F.website);
  const details = [
    get(values, F.publisher),
    mlaDate(parsePartialDate(get(values, F.date))),
    mlaUrl(get(values, F.url)),
  ].filter(Boolean);
  const parts: CitationParts = [italic(title), plain(`${closer(title)} `)];
  if (website) {
    parts.push(italic(website));
    parts.push(plain(details.length > 0 ? ", " : "."));
  }
  if (details.length > 0) {
    parts.push(plain(`${details.join(", ")}.`));
  }
  const locator = mlaLocator(provision);
  const inText: CitationParts = [
    plain("("),
    italic(mlaShortTitle(title)),
    plain(locator ? `, ${locator})` : ")"),
  ];
  return {
    key: "mla",
    label: labels.mla,
    entries: [
      { label: labels.worksCited, parts: tidyParts(parts) },
      { label: labels.inText, parts: inText },
    ],
    note: labels.mlaNote,
  };
}

function legalOutput(
  key: "chicago" | "bluebook",
  provision: Provision,
  labels: ConstitutionLabels
): CitationOutput {
  const label = key === "chicago" ? labels.chicago : labels.bluebook;
  if (provision.part === "whole") {
    return { key, label, entries: [], note: labels.legalWholeNote };
  }
  return {
    key,
    label,
    entries: [
      {
        label: key === "chicago" ? labels.footnote : labels.citation,
        parts: [plain(`${legalConstitutionCite(provision)}.`)],
      },
    ],
    note: key === "chicago" ? labels.chicagoNote : labels.bluebookNote,
  };
}

/** True once there is something to cite: a numbered provision, the preamble or the whole. */
export function constitutionIsReady(values: LegalValues): boolean {
  const part = get(values, F.part) || "article";
  return (
    part === "whole" || part === "preamble" || get(values, F.number) !== ""
  );
}

export function formatConstitution(
  values: LegalValues,
  labels: ConstitutionLabels
): CitationOutput[] {
  const provision = readProvision(values);
  return [
    apaOutput(provision, labels),
    mlaOutput(provision, values, labels),
    legalOutput("chicago", provision, labels),
    legalOutput("bluebook", provision, labels),
  ];
}
